import { consume } from '@lit/context';
import { gsap } from 'gsap';
import { LitElement, css, html } from 'lit';
import { customElement, query } from 'lit/decorators.js';

import { gameApiContext } from '../../context';
import type { GameAPIType } from '../../GameAPI';
import { tokens } from '../../theme/tokens.css';
import { uiState as uiStateProxy, type IdentityProvider } from '../../UIState';
import { bindValtio } from '../../ValtioController';

/** The id is both GameAPI.login's provider and the button's class, which
 *  sets its colours and icon in the stylesheet. */
interface OAuthProvider {
  id: IdentityProvider;
  name: string;
}

/** Order determines display order in the panel. */
const PROVIDERS: readonly OAuthProvider[] = [
  { id: 'microsoft', name: 'Microsoft' },
  { id: 'google', name: 'Google' },
  { id: 'twitter', name: 'X' },
  { id: 'reddit', name: 'Reddit' },
  { id: 'twitch', name: 'Twitch' },
];

@customElement('login-selector-panel')
export class LoginSelectorPanel extends LitElement {
  static override styles = [
    tokens,
    css`
      :host {
        display: block;
        /* Declared here rather than inherited from the body: the panel owns
           its type, and a tap to close it shouldn't start a text selection,
           callout or a grey flash across the whole screen. */
        font-family: var(--font-family);
        -webkit-user-select: none;
        user-select: none;
        -webkit-touch-callout: none;
        -webkit-tap-highlight-color: transparent;
      }

      .backdrop {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: var(--color-bg-backdrop);
        /* Drops the 300ms double-tap delay from tap-to-close. */
        touch-action: manipulation;
      }

      .panel {
        position: fixed;
        box-sizing: border-box;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: min(320px, 92vw);
        /* Internal scroll when the panel is taller than the viewport
           (landscape phones especially). dvh adapts to mobile URL-bar
           collapse; vh fallback for pre-2022 browsers. */
        max-height: calc(100vh - 32px);
        max-height: calc(100dvh - 32px);
        overflow-y: auto;
        overscroll-behavior: contain;
        background-color: var(--color-bg-modal);
        box-shadow: var(--shadow-panel);
        border-radius: var(--radius-lg);
        border-bottom: 2px solid var(--color-border-strong);
        border-top: 2px solid var(--color-border-light);
        cursor: default;
      }

      .desc {
        font-size: var(--font-size-xs);
        text-align: center;
        color: var(--color-text-tooltip);
        padding: 16px 0 12px 0;
        line-height: 1.5;
      }

      .signinwith {
        font-size: var(--font-size-16);
        text-align: center;
        color: var(--color-text-bright);
        margin-top: 8px;
        margin-bottom: 10px;
      }

      .buttons {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        gap: 10px;
        padding: 0 70px;
      }

      /* Per-button bg color is supplied via --btn-bg / --btn-bg-hover CSS
         variables, set by each provider's class below. Eliminates the old
         mouseenter/mouseleave JS handlers that caused sticky-hover on touch
         devices. */
      .button {
        display: flex;
        align-items: center;
        gap: 10px;
        width: 100%;
        padding: 14px 16px;
        background-color: var(--btn-bg);
        border-radius: var(--radius-sm);
        font-size: var(--font-size-md);
        font-weight: bold;
        font-family: var(--font-family);
        cursor: pointer;
        color: var(--color-text-white);
        text-shadow: var(--shadow-text-btn);
        border: none;
        border-bottom: 2px solid var(--color-border-soft);
        box-sizing: border-box;
      }

      /* Brand colours and icon, one class per provider id */
      .button.microsoft {
        --btn-bg: #308c30;
        --btn-bg-hover: #42a042;
        --btn-icon: url('/vanilla/ui/social-logins/microsoft.svg');
      }

      .button.google {
        --btn-bg: #d45541;
        --btn-bg-hover: #d6624f;
        --btn-icon: url('/vanilla/ui/social-logins/google.svg');
      }

      .button.twitter {
        --btn-bg: #000000;
        --btn-bg-hover: #111111;
        --btn-icon: url('/vanilla/ui/social-logins/x.svg');
      }

      .button.reddit {
        --btn-bg: #848484;
        --btn-bg-hover: #9a9a9a;
        --btn-icon: url('/vanilla/ui/social-logins/reddit.svg');
      }

      .button.twitch {
        --btn-bg: #8f73dc;
        --btn-bg-hover: #9c83e0;
        --btn-icon: url('/vanilla/ui/social-logins/twitch.svg');
      }

      .button:active {
        border-bottom: none;
        border-top: 2px solid var(--color-border-strong);
      }

      @media (hover: hover) {
        .button:hover {
          background-color: var(--btn-bg-hover);
          box-shadow: var(--shadow-btn-hover-soft);
        }
      }

      .button .icon {
        display: block;
        width: 20px;
        height: 20px;
        flex-shrink: 0;
        background-image: var(--btn-icon);
        background-repeat: no-repeat;
        background-size: contain;
        filter: brightness(0) invert(1);
      }

      /* 2px-wide separator between icon and label (border-left + border-right
         on a zero-width box renders as two adjacent vertical lines, giving the
         button face an inset look). */
      .button .divider {
        width: 0;
        height: 26px;
        flex-shrink: 0;
        border-left: 1px solid rgba(0, 0, 0, 0.15);
        border-right: 1px solid var(--color-overlay-subtle);
      }

      .button .label {
        flex: 1;
        text-align: center;
      }

      .disclaimer {
        font-size: var(--font-size-xs);
        text-align: center;
        color: var(--color-text-muted);
        padding: 12px 0 16px 0;
        line-height: 1.5;
      }

      .disclaimer a {
        color: var(--color-text-link);
        text-decoration: none;
        outline: none;
      }

      @media (hover: hover) {
        .disclaimer a:hover {
          color: var(--color-text-white);
          box-shadow: 0 1px 0 0 var(--color-text-white);
        }
      }
    `,
  ];

  private uiState = bindValtio(this, uiStateProxy);

  @consume({ context: gameApiContext })
  private api!: GameAPIType;

  @query('.panel') private panel?: HTMLElement;
  @query('.backdrop') private backdrop?: HTMLElement;

  /** Guard against re-entrant close calls (double Escape / backdrop click). */
  private isClosing = false;

  override connectedCallback() {
    super.connectedCallback();
    document.addEventListener('keydown', this.handleKeyDown);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.handleKeyDown);
    if (this.panel) gsap.killTweensOf(this.panel);
    if (this.backdrop) gsap.killTweensOf(this.backdrop);
  }

  /** Guard, don't reset: a close that lands before first render must not be
   *  undone into a ghost entrance. */
  override firstUpdated() {
    if (this.isClosing) return;
    this.animateIn();
  }

  /** A successful sign-in closes it, as legacy's loginSuccess did: the lobby
   *  turns signed-in underneath once the account's settings arrive. */
  override updated() {
    if (this.uiState.isLoggedIn) this.close();
  }

  private animateIn() {
    if (!this.panel || !this.backdrop) return;
    gsap.killTweensOf(this.backdrop);
    gsap.killTweensOf(this.panel);
    gsap.fromTo(this.backdrop, { opacity: 0 }, { opacity: 1, duration: 0.3, ease: 'power2.out' });
    gsap.fromTo(this.panel, { opacity: 0, scale: 0.9 }, { opacity: 1, scale: 1, duration: 0.4, ease: 'power2.out' });
  }

  /** @param onComplete - invoked after the out-animation completes */
  private animateOut(onComplete: () => void) {
    if (!this.panel || !this.backdrop) {
      onComplete();
      return;
    }

    gsap.killTweensOf(this.backdrop);
    gsap.killTweensOf(this.panel);
    gsap.to(this.backdrop, { opacity: 0, duration: 0.3, ease: 'power2.in' });
    gsap.to(this.panel, { opacity: 0, scale: 0.9, duration: 0.3, ease: 'power2.in', onComplete });
  }

  private handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      this.close();
    }
  };

  private handleBackdropClick = () => {
    this.close();
  };

  private close() {
    if (this.isClosing) return;
    this.isClosing = true;
    this.animateOut(() => {
      this.api.closePanel();
    });
  }

  /** Panel stays open after click — closes on OAuth success or explicit dismiss. */
  private handleProviderClick(providerId: IdentityProvider) {
    this.api.playClick();
    this.api.login(providerId);
  }

  override render() {
    return html`
      <div class="backdrop" @click=${this.handleBackdropClick}></div>
      <div class="panel" @click=${(e: Event) => e.stopPropagation()}>
        <div class="desc">
          Use any of the following services to sign in<br />
          to save your account progress and<br />
          achievements.
        </div>
        <div class="signinwith">Sign in with</div>
        <div class="buttons">
          ${PROVIDERS.map(
            (provider) => html`
              <button class="button ${provider.id}" @click=${() => this.handleProviderClick(provider.id)}>
                <span class="icon"></span>
                <span class="divider"></span>
                <span class="label">${provider.name}</span>
              </button>
            `,
          )}
        </div>
        <div class="disclaimer">
          We only require the absolute minimum<br />
          permission set that each platform provides.<br />
          See our <a href="/privacy.html" target="_blank" rel="noopener">Privacy Policy</a> for more.
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'login-selector-panel': LoginSelectorPanel;
  }
}
